import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { NotificationType } from "@/hooks/use-notifications";

/**
 * Per-user in-app notification preferences (Settings → Notifications).
 *
 * The server owns the list of controllable types — GET /preferences returns
 * EVERY controllable type with its effective in-app value (default ON when the
 * user has no explicit row). So the client just renders whatever comes back and
 * never hardcodes the type list.
 *
 * Mirrors worker/routes/notifications.js  GET/PATCH /api/notifications/preferences.
 */

const PREFS_KEY = ["notifications", "preferences"] as const;

export type NotificationPreference = {
  type: NotificationType;
  inApp: boolean;
};

/** GET /api/notifications/preferences → controllable types with effective values. */
export function useNotificationPreferences() {
  return useQuery<NotificationPreference[]>({
    queryKey: PREFS_KEY,
    queryFn: async () => {
      const res = await api.get<{ preferences: NotificationPreference[] }>(
        "/notifications/preferences",
      );
      return res.preferences;
    },
    staleTime: 60_000,
  });
}

/**
 * PATCH /api/notifications/preferences { type, inApp }.
 * Optimistically flips the toggle so the switch feels instant, rolling back on
 * error. Re-fetches on settle to reconcile with the server.
 */
export function useUpdateNotificationPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: { type: NotificationType; inApp: boolean }) =>
      api.patch<{ ok: true; type: string; inApp: boolean }>(
        "/notifications/preferences",
        vars,
      ),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: PREFS_KEY });
      const prev = qc.getQueryData<NotificationPreference[]>(PREFS_KEY);
      if (prev) {
        qc.setQueryData<NotificationPreference[]>(
          PREFS_KEY,
          prev.map((p) => (p.type === vars.type ? { ...p, inApp: vars.inApp } : p)),
        );
      }
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(PREFS_KEY, ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: PREFS_KEY });
    },
  });
}
